// HSSNA API 拦截器 — 请求/响应拦截
// 规格: SPEC §4.1 API 客户端架构 / §4.2 请求头注入规约 / §4.4 RFC 7807 / §7 安全合规
//
// 任务: 战役 1 T1-14
//
// 请求拦截器 (SPEC §4.2):
//   - Authorization: Bearer <token>            (authClient.getValidAccessToken, 自动 refresh)
//   - X-Request-Id: <uuid v4>                   (客户端生成)
//   - X-Trace-Id: <32-hex>                      (W3C Trace Context, 如不存在)
//   - Idempotency-Key: <uuid v4>                (仅写操作 POST/PUT/PATCH/DELETE, H 硬约束)
//   - User-Agent: <service>/<version>
//   - Accept-Language: zh-CN
//   - 不注入 X-Family-Group-Id                  (网关从 JWT claim 重写, C-SEC-03, 前端禁发送)
//
// 响应拦截器:
//   - 401 → 触发 token refresh + 重试 (单次, 防循环)
//     ★ 并发请求挂起队列: 多个请求同时收到 401 时, 仅首个触发 refresh,
//       其余请求挂起等待 refresh 完成后统一用新 token 重试 (避免 refresh 风暴)
//   - RFC 7807 Problem Details 解析 (parseProblemDetails)
//   - handleError(error) 动作路由
//   - IDOR (HSSNA-403-002) 自动上报 (console.error + Sentry 钩子)

import type { AuthClient } from '../auth/index.js';
import {
  ErrorCode,
  parseProblemDetails,
  dispatchGlobalError,
  type HssnaError,
} from '../errors/index.js';

/** 写操作方法集 (需注入 Idempotency-Key) */
const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** 默认服务版本 */
const DEFAULT_VERSION = '0.1.0';

/**
 * service 名 → X-Client-Role 映射 (M2-T05 多客户端角色隔离)
 * 前端各端通过 service 名标识自身, 后端 ClientRoleMiddleware 校验此 header.
 */
const SERVICE_TO_CLIENT_ROLE: Record<string, string> = {
  'family-web': 'family-app',
  'family-app': 'family-app',
  'elder-screen': 'elder-screen',
  'medical-cws': 'caregiver-app',
  'nurse-app': 'caregiver-app',
  'ops-console': 'admin-portal',
  'ems-terminal': 'ems-dispatcher',
};

/** 请求配置 (扩展 RequestInit, 标准化 url 与 headers) */
export interface RequestConfig extends RequestInit {
  /** 请求 URL (绝对路径或相对路径) */
  url: string;
  /** 已规范化的 headers (Record 形式, 便于拦截器读写) */
  headers: Record<string, string>;
  /** 内部: 401 重试计数 (防止 refresh + 重试循环) */
  _retryCount?: number;
}

/** 拦截器创建选项 */
export interface InterceptorOptions {
  /** 认证客户端 (提供 getValidAccessToken / refresh) */
  readonly authClient: AuthClient;
  /** 服务名 (用于 User-Agent 标识, 如 'medical-cws' / 'family-web') */
  readonly service: string;
  /** 服务版本 (默认 0.1.0) */
  readonly version?: string;
  /**
   * 自定义 IDOR 安全事件上报钩子
   * 默认: console.error + 全局 Sentry (如可用)
   * 各端可注入 @sentry/react 或 @sentry/browser 的 captureException
   */
  readonly reportSecurityIncident?: (error: HssnaError) => void;
}

/** 拦截器集合 */
export interface Interceptors {
  /** 请求拦截器: 注入 JWT / Trace-Id / Request-Id / Idempotency-Key / User-Agent */
  request: (config: RequestConfig) => Promise<RequestConfig>;
  /**
   * 响应拦截器: 401 refresh + 重试, RFC 7807 解析, IDOR 上报
   * @param response fetch 响应
   * @param config   已注入头的请求配置 (可变, retry 时复用)
   * @param retry    重试函数 (调用以重新发送请求, 使用 config 最新状态)
   */
  response: (
    response: Response,
    config: RequestConfig,
    retry: () => Promise<Response>,
  ) => Promise<Response>;
}

/**
 * 创建请求/响应拦截器
 *
 * @example
 * ```ts
 * const interceptors = createInterceptors({
 *   authClient,
 *   service: 'medical-cws',
 *   version: '0.1.0',
 * });
 * const fetchClient = createFetchClient(interceptors);
 * const res = await fetchClient('/v1/alerts', { method: 'GET' });
 * ```
 */
export function createInterceptors(options: InterceptorOptions): Interceptors {
  const {
    authClient,
    service,
    version = DEFAULT_VERSION,
    reportSecurityIncident,
  } = options;
  const userAgent = `${service}/${version}`;
  const reportIncident = reportSecurityIncident ?? defaultReportIncident;

  // ===== 401 并发请求挂起队列 =====
  // 当首个 401 触发 refresh 时, 后续 401 请求挂起等待同一 refreshPromise;
  // refresh 成功 → 队列中的请求用新 token 重试; 失败 → 全部走 handleError.
  let refreshPromise: Promise<string | null> | null = null;

  /** 触发或复用正在进行的 refresh (单例去重) */
  const triggerRefresh = (): Promise<string | null> => {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      try {
        await authClient.refresh();
        return await authClient.getValidAccessToken();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[HSSNA] token refresh failed on 401:', err);
        // P1-9: 清除陈旧 token, 避免后续请求携带无效 token 形成 401 循环
        // dispatchGlobalError 会派发 redirect-login 动作, 引导用户重新登录
        await authClient.getTokenStore().clear();
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
    return refreshPromise;
  };

  return {
    // ===== 请求拦截器 =====
    request: async (config) => {
      const headers: Record<string, string> = { ...config.headers };

      // 1. Authorization: Bearer <token>
      //    authClient.getValidAccessToken 内部自动 refresh (过期前 60s)
      const token = await authClient.getValidAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // 2. X-Request-Id: <uuid v4> (客户端生成, 每次请求唯一)
      if (!headers['X-Request-Id']) {
        headers['X-Request-Id'] = generateUuidV4();
      }

      // 3. X-Trace-Id: <32-hex> (W3C Trace Context, 如不存在则生成)
      if (!headers['X-Trace-Id']) {
        headers['X-Trace-Id'] = generateTraceId();
      }

      // 4. Idempotency-Key: <uuid v4> (仅写操作, H 硬约束, TTL 24h)
      const method = (config.method ?? 'GET').toUpperCase();
      if (WRITE_METHODS.has(method) && !headers['Idempotency-Key']) {
        headers['Idempotency-Key'] = generateUuidV4();
      }

      // 5. User-Agent: <service>/<version>
      if (!headers['User-Agent']) {
        headers['User-Agent'] = userAgent;
      }

      // 6. Accept-Language: zh-CN (固定)
      if (!headers['Accept-Language']) {
        headers['Accept-Language'] = 'zh-CN';
      }

      // 7. X-Client-Role: 根据 service 名映射 (M2-T05 多客户端角色隔离)
      //    后端 ClientRoleMiddleware 校验此 header, 缺失返回 403.
      if (!headers['X-Client-Role']) {
        const role = SERVICE_TO_CLIENT_ROLE[service];
        if (role) {
          headers['X-Client-Role'] = role;
        }
      }

      // 不注入 X-Family-Group-Id — 网关从 JWT claim 重写 (C-SEC-03, 前端禁发送)

      return { ...config, headers };
    },

    // ===== 响应拦截器 =====
    response: async (response, config, retry) => {
      // 成功响应直接返回 (2xx)
      if (response.ok) {
        return response;
      }

      // 解析 RFC 7807 Problem Details → HssnaError
      // 使用 clone() 以免消费 body 影响调用方
      const error = await parseProblemDetails(response.clone());

      // IDOR (HSSNA-403-002) 自动上报 — 安全事件 (NF-SEC-04 / C-SEC-08)
      if (error.code === ErrorCode.HSSNA_403_002) {
        reportIncident(error);
      }

      // 401 → 挂起到统一 refresh 队列, 成功后用新 token 重试 (单次, 防循环)
      if (response.status === 401) {
        const retryCount = config._retryCount ?? 0;
        if (retryCount < 1) {
          // ★ 挂起当前请求, 等待 refresh 完成 (首个请求触发 refresh, 后续复用同一 promise)
          const newToken = await triggerRefresh();
          if (newToken) {
            config.headers['Authorization'] = `Bearer ${newToken}`;
            config._retryCount = retryCount + 1;
            // 重试: 由 fetch-client 使用更新后的 config 重新发送
            return await retry();
          }
          // refresh 失败 (newToken === null) — 走 handleError 路由 (通常 redirect-login)
        }
      }

      // handleError 动作路由 (SPEC §4.4.4)
      // 派发到全局错误处理中心 (showToast/showModal/redirectLogin 等)
      // dispatchGlobalError 是异步的但不需要 await — 不阻塞 throw 流程
      void dispatchGlobalError(error);

      // 抛出 HssnaError 供调用方捕获 (如 TanStack Query onError)
      throw error;
    },
  };
}

/**
 * 默认 IDOR 安全事件上报
 * - console.error (SPEC §6.2 允许 console.error/warn)
 * - Sentry 钩子 (如全局已注入 @sentry/* SDK)
 */
function defaultReportIncident(error: HssnaError): void {
  // eslint-disable-next-line no-console
  console.error('[HSSNA] IDOR 安全事件上报 (HSSNA-403-002):', {
    code: error.code,
    title: error.title,
    detail: error.detail,
    instance: error.instance,
    traceId: error.traceId,
  });

  // Sentry 钩子 (各端通过 @sentry/react 或 @sentry/browser 注入全局 Sentry)
  const globalSentry = (
    globalThis as { Sentry?: { captureException: (e: unknown) => void } }
  ).Sentry;
  if (globalSentry && typeof globalSentry.captureException === 'function') {
    globalSentry.captureException(error);
  }
}

/**
 * 生成 UUID v4 (优先 crypto.randomUUID, 回退基于时间戳+随机数)
 * 用于 X-Request-Id / Idempotency-Key
 */
function generateUuidV4(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // 回退 (非安全上下文 / 旧环境)
  const ts = Date.now().toString(16);
  const rand = Math.random().toString(16).slice(2, 18);
  return `${ts}-${rand}`;
}

/**
 * 生成 32-hex Trace-Id (W3C Trace Context 格式)
 * 用于 X-Trace-Id
 */
function generateTraceId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  ) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  // 回退: 32 位 hex
  let hex = '';
  for (let i = 0; i < 32; i++) {
    hex += Math.floor(Math.random() * 16).toString(16);
  }
  return hex;
}
